import { useState, type FormEvent } from 'react';
import { Briefcase, FileText, SpinnerGap, Envelope, UserList, X } from '@phosphor-icons/react';
import { useAddEndClient, type AddEndClientInput } from '@/lib/end-clients/useAddEndClient';

const EMPTY: AddEndClientInput = {
  name: '',
  email: '',
  job_title: '',
  target_company: '',
  linkedin_url: '',
  notes: '',
};

export function AddEndClientDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const add = useAddEndClient();
  const [form, setForm] = useState<AddEndClientInput>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!open) return null;

  function patch<K extends keyof AddEndClientInput>(key: K, value: AddEndClientInput[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function reset() {
    setForm(EMPTY);
    setError(null);
    setSuccess(null);
  }

  function close() {
    if (add.isPending) return;
    reset();
    onClose();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are required.');
      return;
    }

    try {
      const created = await add.mutateAsync(form);
      setSuccess(`${(created as { name?: string }).name || 'Candidate'} added successfully.`);
      setForm(EMPTY);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Add candidate"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-xl rounded-2xl border border-white/10 bg-neurax-midnight p-6 shadow-glow">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-neurax-electric/50 via-neurax-plasma/50 to-transparent" />

        <button
          type="button"
          onClick={close}
          disabled={add.isPending}
          className="absolute right-4 top-4 rounded-lg p-1.5 text-muted-foreground transition hover:bg-white/5 hover:text-white disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-5 flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-neurax-electric/30 to-neurax-plasma/30 text-neurax-electric">
            <UserList className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-white">Add candidate</h2>
            <p className="text-xs text-muted-foreground">
              Add a candidate to your roster — you can issue them a NeuraX license afterward.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Full name"
              required
              value={form.name}
              onChange={(v) => patch('name', v)}
              placeholder="Asha Rao"
              autoFocus
              disabled={add.isPending}
            />
            <Field
              label="Email"
              required
              type="email"
              icon={<Envelope className="h-4 w-4 text-muted-foreground" />}
              value={form.email}
              onChange={(v) => patch('email', v)}
              placeholder="asha@example.com"
              disabled={add.isPending}
            />
          </div>

          <Field
            label="Role / job title"
            icon={<Briefcase className="h-4 w-4 text-muted-foreground" />}
            value={form.job_title}
            onChange={(v) => patch('job_title', v)}
            placeholder="Senior Software Engineer"
            disabled={add.isPending}
          />

          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <FileText className="h-3.5 w-3.5" />
              Notes
            </label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => patch('notes', e.target.value)}
              placeholder="Anything the team should know — interview status, special prep needs, etc."
              disabled={add.isPending}
              className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-neurax-electric/30 disabled:opacity-50"
            />
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              {error}
            </div>
          )}
          {success && (
            <div
              role="status"
              className="rounded-lg border border-neurax-mint/30 bg-neurax-mint/10 px-3 py-2 text-xs text-neurax-mint"
            >
              {success}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={close}
              disabled={add.isPending}
              className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-medium text-muted-foreground transition hover:text-white disabled:opacity-50"
            >
              {success ? 'Done' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={add.isPending || !form.name.trim() || !form.email.trim()}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110 disabled:opacity-60"
            >
              {add.isPending ? (
                <>
                  <SpinnerGap className="h-4 w-4 animate-spin" />
                  Adding…
                </>
              ) : (
                'Add candidate'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
  type = 'text',
  icon,
  autoFocus,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  type?: 'text' | 'email';
  icon?: React.ReactNode;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
        {required && <span className="ml-1 text-destructive">*</span>}
      </label>
      <div className="relative">
        {icon && (
          <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">{icon}</div>
        )}
        <input
          type={type}
          required={required}
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full rounded-xl border border-white/10 bg-white/[0.04] py-2.5 ${icon ? 'pl-10' : 'pl-4'} pr-4 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-neurax-electric/30 disabled:opacity-50`}
        />
      </div>
    </div>
  );
}
